import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ACQUISITION_OPTIONS, type AcquisitionSource } from '@/lib/onboarding';

interface OnboardingSegmentationProps {
  open: boolean;
  submitting?: boolean;
  onContinue: (source: AcquisitionSource) => void;
  /** When true the dialog can be closed — used when reopened from the dashboard banner. */
  dismissible?: boolean;
  onDismiss?: () => void;
}

export function OnboardingSegmentation({
  open,
  submitting,
  onContinue,
  dismissible = false,
  onDismiss,
}: OnboardingSegmentationProps) {
  const [selected, setSelected] = useState<AcquisitionSource | null>(null);

  return (
    <Dialog open={open} onOpenChange={dismissible ? (o) => { if (!o) onDismiss?.(); } : undefined}>
      <DialogContent
        className="sm:max-w-md gap-4"
        onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
        hideCloseButton={!dismissible}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">Antes de empezar, cuéntanos algo</DialogTitle>
          <DialogDescription>Para personalizar tu experiencia en FOCO.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5">
          {ACQUISITION_OPTIONS.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelected(opt.value)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-xl border p-4 text-left transition-colors touch-manipulation',
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'hover:bg-muted/50',
                )}
                aria-pressed={isSelected}
              >
                <span className="text-2xl shrink-0" aria-hidden>
                  {opt.emoji}
                </span>
                <span className="text-sm font-medium">{opt.label}</span>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            type="button"
            className="w-full gap-2"
            disabled={!selected || submitting}
            onClick={() => selected && onContinue(selected)}
          >
            {submitting ? 'Guardando…' : 'Continuar'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
